/**
 * Password-based encryption of the vault payload — AES-256-GCM with a PBKDF2-SHA256 key.
 *
 * WebCrypto only: `crypto.subtle` runs PBKDF2 in native code, which is both much faster
 * (so we can afford a high iteration count) and immune to the JS engine leaving copies of
 * the derived key around. `crypto.subtle` requires a secure context — HTTPS or localhost.
 *
 * A wrong password is indistinguishable from a corrupt ciphertext at the AES-GCM level:
 * both surface as an `OperationError`. There is no server round-trip to ask, so the
 * decryption failure IS the "wrong password" signal — hence {@link WrongPasswordError}.
 */

import { utf8ToBytes } from './bytes';
import { CryptoUnavailableError, WrongPasswordError } from './errors';

/** PBKDF2 iteration count. Floor mandated by the module's threat model; never lower it. */
export const PBKDF2_ITERATIONS = 600000;
/** Name recorded in the vault so a future version can migrate old records. */
export const KDF_NAME = 'PBKDF2-SHA256';
export const SALT_LENGTH = 16;
export const IV_LENGTH = 12;
export const AES_KEY_BITS = 256;
export const GCM_TAG_BITS = 128;

interface GlobalWithCrypto {
  crypto?: Crypto;
}

function getCrypto(): Crypto {
  const scope = (typeof globalThis !== 'undefined' ? globalThis : window) as unknown as GlobalWithCrypto;
  const c = scope.crypto;
  if (!c || !c.subtle || typeof c.getRandomValues !== 'function') {
    throw new CryptoUnavailableError();
  }
  return c;
}

/** True when this environment can create/unlock vaults. */
export function isWebCryptoAvailable(): boolean {
  try {
    getCrypto();
    return true;
  } catch (err) {
    return false;
  }
}

/** Cryptographically strong random bytes. */
export function randomBytes(length: number): Uint8Array {
  const out = new Uint8Array(length);
  getCrypto().getRandomValues(out);
  return out;
}

/**
 * Additional authenticated data. Binding the ciphertext to `accountId` + version means a
 * vault blob copied onto another account's record fails to decrypt instead of unlocking
 * a session that claims the wrong identity.
 */
function additionalData(accountId: string, version: number): Uint8Array {
  return utf8ToBytes(`nuxaris-vault:v${version}:${accountId}`);
}

async function deriveAesKey(
  password: string,
  salt: Uint8Array,
  iterations: number,
  usage: KeyUsage,
): Promise<CryptoKey> {
  const subtle = getCrypto().subtle;
  const passwordBytes = utf8ToBytes(password);
  const baseKey = await subtle.importKey('raw', passwordBytes, 'PBKDF2', false, ['deriveKey']);
  return subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: AES_KEY_BITS },
    false, // non-extractable: the AES key can never be read back out of WebCrypto
    [usage],
  );
}

export interface EncryptedPayload {
  salt: Uint8Array;
  iv: Uint8Array;
  ciphertext: Uint8Array;
  iterations: number;
}

/** Encrypts `plaintext` under `password`, generating a fresh salt and IV. */
export async function encryptPayload(
  plaintext: Uint8Array,
  password: string,
  accountId: string,
  version: number,
  iterations: number = PBKDF2_ITERATIONS,
): Promise<EncryptedPayload> {
  const salt = randomBytes(SALT_LENGTH);
  const iv = randomBytes(IV_LENGTH);
  const key = await deriveAesKey(password, salt, iterations, 'encrypt');
  const ciphertext = await getCrypto().subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: additionalData(accountId, version), tagLength: GCM_TAG_BITS },
    key,
    plaintext,
  );
  return { salt, iv, ciphertext: new Uint8Array(ciphertext), iterations };
}

/**
 * Decrypts a payload. Any authentication failure (wrong password, tampered ciphertext,
 * mismatched accountId) becomes {@link WrongPasswordError} — the caller cannot tell them
 * apart, and neither can we.
 */
export async function decryptPayload(
  payload: EncryptedPayload,
  password: string,
  accountId: string,
  version: number,
): Promise<Uint8Array> {
  const key = await deriveAesKey(password, payload.salt, payload.iterations, 'decrypt');
  try {
    const plaintext = await getCrypto().subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: payload.iv,
        additionalData: additionalData(accountId, version),
        tagLength: GCM_TAG_BITS,
      },
      key,
      payload.ciphertext,
    );
    return new Uint8Array(plaintext);
  } catch (err) {
    throw new WrongPasswordError();
  }
}
